import React from "react";
import { Calculator, Info } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { fmtINR } from "../../lib/api";

/**
 * Phase 207 — head-wise tax table (Arrear / Current / Total per tax head + Total Demand).
 * Used for the citizen's estimate (arrears are 0) and for the final demand lines at every review stage.
 */
export function SafTaxTable({ lines = [], testId = "saf-tax-table", caption, highlight = false }) {
  const num = (v) => Number(v) || 0;
  const tot = lines.reduce((a, ln) => ({ arrear: a.arrear + num(ln.arrear_amount), current: a.current + num(ln.current_amount) }), { arrear: 0, current: 0 });
  return (
    <div className="border rounded-lg overflow-x-auto" data-testid={testId}>
      {caption && <p className="text-xs font-semibold px-3 pt-2.5 pb-1">{caption}</p>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Tax head</TableHead>
            <TableHead className="text-xs text-right">Arrear (₹)</TableHead>
            <TableHead className="text-xs text-right">Current (₹)</TableHead>
            <TableHead className="text-xs text-right">Total (₹)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.length === 0 && (
            <TableRow><TableCell colSpan={4} className="text-xs text-muted-foreground text-center py-4">No tax lines</TableCell></TableRow>
          )}
          {lines.map((ln, i) => (
            <TableRow key={i} data-testid={`${testId}-row-${i}`}>
              <TableCell className="text-sm">
                <span className="font-medium">{ln.tax_head_name || ln.label || "Tax"}</span>
                {ln.usage_label && <span className="text-muted-foreground text-xs"> ({ln.usage_label})</span>}
                {(ln.fy_label || ln.percent) && (
                  <p className="text-[11px] text-muted-foreground">{[ln.fy_label ? `FY ${ln.fy_label}` : null, ln.percent ? `${ln.percent}% of ARV` : null].filter(Boolean).join(" · ")}</p>
                )}
              </TableCell>
              <TableCell className="text-sm text-right tabular-nums" data-testid={`${testId}-arrear-${i}`}>{fmtINR(num(ln.arrear_amount))}</TableCell>
              <TableCell className="text-sm text-right tabular-nums" data-testid={`${testId}-current-${i}`}>{fmtINR(num(ln.current_amount))}</TableCell>
              <TableCell className="text-sm text-right tabular-nums font-medium">{fmtINR(num(ln.arrear_amount) + num(ln.current_amount))}</TableCell>
            </TableRow>
          ))}
          <TableRow className={`font-semibold ${highlight ? "bg-primary/5" : "bg-muted/60"}`} data-testid={`${testId}-total-row`}>
            <TableCell className="text-sm">Total Demand</TableCell>
            <TableCell className="text-sm text-right tabular-nums" data-testid={`${testId}-total-arrear`}>{fmtINR(tot.arrear)}</TableCell>
            <TableCell className="text-sm text-right tabular-nums" data-testid={`${testId}-total-current`}>{fmtINR(tot.current)}</TableCell>
            <TableCell className="text-base text-right tabular-nums" data-testid={`${testId}-total`}>{fmtINR(tot.arrear + tot.current)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export function SafTaxEstimate({ estimate, testId = "saf-estimate" }) {
  if (!estimate) return null;
  const labels = (estimate.fy_labels && estimate.fy_labels.length ? estimate.fy_labels : [estimate.fy_label]).filter(Boolean);
  const perYear = estimate.per_year || [];
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium flex items-center gap-2" data-testid={`${testId}-heading`}>
          <Calculator className="h-4 w-4" /> Estimated tax — FY {labels.join(", ")}
        </p>
        <p className="text-xs text-muted-foreground">
          {estimate.road_width_label ? `Road width ${estimate.road_width_label} · ` : ""}Annual value (ARV) {fmtINR(estimate.arv_total_yearly || 0)}
        </p>
      </div>
      {estimate.multi_year && perYear.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-per-year`}>
          {perYear.map((y) => (
            <span key={y.financial_year_id} className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-1 text-[11px]" data-testid={`${testId}-year-${y.fy_label}`}>
              <span className="font-medium">FY {y.fy_label}</span>
              <span className="text-muted-foreground">·</span>
              <span className="tabular-nums">{y.lines > 0 ? fmtINR(y.total_current || 0) : "no rates"}</span>
            </span>
          ))}
        </div>
      )}
      <SafTaxTable lines={estimate.lines || []} testId={`${testId}-table`} highlight />
      {(estimate.warnings || []).length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 space-y-0.5" data-testid={`${testId}-warnings`}>
          {estimate.warnings.map((w, i) => <p key={i}>{w}</p>)}
        </div>
      )}
      <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid={`${testId}-disclaimer`}>
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>{estimate.disclaimer || "This is only an estimate. The final demand is decided by the Field Collector during physical verification and approved by the Supervisor and the Municipal Admin."}</span>
      </p>
    </div>
  );
}
